import { CheckSquare, Trash, XSquare } from 'react-feather';
import { DocumentNode } from 'graphql';
import { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import { GetMailPermissionsQuery as GetMailPermissions } from '__generated__/graphql';

import { ContainerWithOptions, Option } from '../../../common/Common/OptionsDropdown';
import { assetUrl, encodeUrl } from '../../../common/helpers';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import AddMailPermissionForm from './AddMailPermissionForm';
import DeleteMailPermissionMutation from './DeleteMailPermissionMutation';
import EditMailPermissionForm from './EditMailPermissionForm';
import GetMailPermissionsQuery from './GetMailPermissionsQuery';
import UI from '../../../common/UI';
import useConfirmModal from '../../../common/useConfirmModal';
import useLocale from '../../../common/useLocale';
import useMutation from '../../../api/useMutation';
import useProject from '../../useProject';
import useQuery from '../../../api/useQuery';

type MailPermission = GetMailPermissions['project']['mail_permissions'][0];

interface MailPermissionPageProps {
  mailPermissionId?: 'add' | string;
}

interface MailPermissionPreviewProps {
  mailPermission: MailPermission;
  refetchQueries?: DocumentNode[];
}

const MailPermissionPreview = ({
  mailPermission, refetchQueries = [GetMailPermissionsQuery],
}: MailPermissionPreviewProps) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();
  const notifier = useNotifier();
  const history = useHistory();
  const project = useProject();

  const [deleteMailPermission, { loading: deleting }] = useMutation(
    DeleteMailPermissionMutation, {
      variables: {
        id: mailPermission.id,
      },
      onCompleted: () => {
        notifier.success(t('notifications.mail_permission_deleted'));

        history.push(`/${project.id}/communication/permissions`);
      },
      onError: () => {
        notifier.error(t('something_went_wrong'));
      },
      refetchQueries,
      awaitRefetchQueries: refetchQueries.length > 0,
    },
  );

  const [confirmDelete] = useConfirmModal({
    title: t('delete_name', { name: mailPermission.title }),
    onConfirm: () => deleteMailPermission(),
  }, [mailPermission]);

  return (
    <ContainerWithOptions
      name={mailPermission.title}
      renderOptions={() => (
        <Option
          icon={<Trash />}
          label={t('delete')}
          onClick={() => confirmDelete()}
          disabled={deleting}
        />
      )}
    >
      <UI.GridContainer sc={{ columns: '6fr 1fr 1fr', gutter: 0.5 }}>
        <UI.Div style={{ minWidth: 0 }}>
          <UI.H4>
            <UI.Truncate>
              {mailPermission.title}
            </UI.Truncate>
          </UI.H4>
        </UI.Div>
        <UI.Div>
          <UI.TooltipContainer sc={{ inlineBlock: true, padding: 1, margin: -1 }}>
            <UI.Link
              to={`/${project.id}/participants/contacts?${encodeUrl({ mail_opt_ins: [mailPermission.id] })}`}
              sc={{ secondary: true }}
            >
              <UI.Icon>
                <CheckSquare />
              </UI.Icon>
              {' '}
              {formatNumber(mailPermission.opt_in_count)}
            </UI.Link>
            <UI.Tooltip sc={{ alignH: 'center' }}>
              {t('opt_ins')}
            </UI.Tooltip>
          </UI.TooltipContainer>
        </UI.Div>
        <UI.Div sc={{ color: 'gray.500' }}>
          <UI.TooltipContainer sc={{ inlineBlock: true, padding: 1, margin: -1 }}>
            <UI.Icon>
              <XSquare />
            </UI.Icon>
            {' '}
            {formatNumber(mailPermission.opt_out_count)}
            <UI.Tooltip sc={{ alignH: 'center' }}>
              {t('opt_outs')}
            </UI.Tooltip>
          </UI.TooltipContainer>
        </UI.Div>
      </UI.GridContainer>
    </ContainerWithOptions>
  );
};

interface MailPermissionCardProps {
  mailPermission: MailPermission;
}

const MailPermissionCard = ({ mailPermission }: MailPermissionCardProps) => {
  const project = useProject();
  const history = useHistory();
  const { mailPermissionId } = useParams<MailPermissionPageProps>();

  const open = mailPermission.id === mailPermissionId;

  const onSuccess = () => {
    history.push(`/${project.id}/communication/permissions`);
  };

  return (
    <UI.CollapsibleNavCard
      to={`/${project.id}/communication/permissions/${!open ? mailPermission.id : ''}`}
      open={open}
      muted={!!mailPermissionId && !open}
      preview={<MailPermissionPreview mailPermission={mailPermission} />}
      body={(
        <EditMailPermissionForm
          mailPermission={mailPermission}
          onSuccess={onSuccess}
        />
      )}
    />
  );
};

const MailPermissionsPage = ({ mailPermissionId }: MailPermissionPageProps) => {
  const { t } = useTranslation();
  const project = useProject();
  const history = useHistory();

  const { data, refetch } = useQuery(GetMailPermissionsQuery, {
    variables: {
      id: project.id,
    },
  });

  const mailPermissions = data?.project.mail_permissions || [];

  const [openAddMailPermissionModal, closeAddMailPermissionModal] = useModal(() => {
    const close = () => {
      closeAddMailPermissionModal();
      history.push(`/${project.id}/communication/permissions`);
    };

    return (
      <UI.Modal maxWidth={600} close={close}>
        <AddMailPermissionForm
          onSuccess={close}
          onCancel={close}
        />
      </UI.Modal>
    );
  }, []);

  useEffect(() => {
    if (mailPermissionId === 'add') {
      openAddMailPermissionModal();
    } else {
      refetch();
    }
  }, [mailPermissionId]);

  if (!data) {
    return <UI.PageLoader />;
  }

  return (
    <UI.Section sc={{ compact: true }}>
      <UI.GridContainer>
        {mailPermissions.length > 0 && (
          <UI.GridContainer sc={{ columns: '1fr 1fr', gutter: 0.5 }}>
            <UI.Div>
              <UI.SectionHeading>{t('mail_permissions')}</UI.SectionHeading>
            </UI.Div>
            <UI.Div sc={{ textAlign: 'right' }}>
              <UI.NavButton
                to={`/${project.id}/communication/permissions/add`}
                sc={{ brand: 'secondary' }}
              >
                {t('add_mail_permission')}
              </UI.NavButton>
            </UI.Div>
          </UI.GridContainer>
        )}

        <UI.GridContainer sc={{ gutter: 1 / 3 }}>
          {mailPermissions.map((mailPermission) => (
            <MailPermissionCard
              mailPermission={mailPermission}
              key={mailPermission.id}
            />
          ))}
          {mailPermissions.length === 0 && (
            <UI.Card sc={{ padding: 8, textAlign: 'center', background: 'gray.50' }} style={{ textWrap: 'balance' }}>
              <img src={assetUrl('images/communicate.svg')} alt="" height={120} />
              <UI.H3 sc={{ mb: 2 }}>{t('no_mail_permissions_yet')}</UI.H3>
              <UI.Paragraph>
                {t('mail_permissions_description')}
              </UI.Paragraph>
              <UI.NavButton
                to={`/${project.id}/communication/permissions/add`}
                sc={{ brand: 'secondary' }}
              >
                {t('add_mail_permission')}
              </UI.NavButton>
            </UI.Card>
          )}
        </UI.GridContainer>
      </UI.GridContainer>
    </UI.Section>
  );
};

export default MailPermissionsPage;
